import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DinoId, DINOS, DINO_ORDER } from '../data/dinos';
import { CommsBox, COMMS } from '../objects/CommsBox';
import {
  fetchRankFor,
  fetchTopScores,
  formatTime,
  isLeaderboardConfigured,
  LeaderboardEntry,
  submitScore,
} from '../services/leaderboard';

interface ClearedInit {
  score: number;
  /** dino that delivered the final blow — usually the last in DINO_ORDER */
  dinoId: DinoId;
  /** Wall-clock timestamp the gauntlet's first leg started — undefined if
   *  this scene is reached via some non-gauntlet path. */
  runStartMs?: number;
}

/**
 * Shown only after a full GAUNTLET run is complete. Single-level practice
 * wins use LevelClearedScene instead.
 *
 * Three phases:
 *   1. Celebration animation: dinos lineup, confetti, score+time tally,
 *      Mr Consequence's final meltdown comms.
 *   2. Score-entry form (NAME + email opt-in for the next prize draw).
 *   3. Top-10 leaderboard with the player's row highlighted.
 */
export class PlanetClearedScene extends Phaser.Scene {
  private payload!: ClearedInit;
  private formEl: HTMLDivElement | null = null;

  constructor() {
    super('PlanetCleared');
  }

  init(data: ClearedInit) {
    this.payload = data;
  }

  create() {
    const { score, dinoId, runStartMs } = this.payload;
    const totalTimeMs = runStartMs ? Date.now() - runStartMs : 0;
    this.cameras.main.setBackgroundColor(0x0d0a1a);

    // Multi-color starfield using all four planet colors.
    for (let i = 0; i < 90; i++) {
      const planet = DINO_ORDER[Phaser.Math.Between(0, 3)];
      this.add
        .image(Phaser.Math.Between(0, GAME_WIDTH), Phaser.Math.Between(0, GAME_HEIGHT), 'px')
        .setAlpha(Phaser.Math.FloatBetween(0.3, 1))
        .setTint(Math.random() < 0.3 ? DINOS[planet].stripe : 0xffffff);
    }

    // GAUNTLET CLEARED banner — hits big, settles small.
    const title = this.add
      .text(GAME_WIDTH / 2, 28, 'GAUNTLET CLEARED', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '16px',
        color: '#ffe23a',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setAlpha(0);
    this.tweens.add({
      targets: title,
      alpha: 1,
      scale: { from: 1.6, to: 1 },
      duration: 600,
      ease: 'Back.easeOut',
    });

    // Hero lineup — the whole crew, side by side, each pulsing in their
    // own planet color when they enter.
    const lineupY = 84;
    DINO_ORDER.forEach((id, i) => {
      const cx = GAME_WIDTH / 2 + (i - 1.5) * 36;
      const sprite = this.add
        .sprite(cx, lineupY, `dino-${id}`)
        .setScale(2.5)
        .play(`${id}-idle`)
        .setAlpha(0);
      this.tweens.add({
        targets: sprite,
        alpha: 1,
        y: { from: lineupY - 14, to: lineupY },
        duration: 320,
        delay: 350 + i * 140,
        ease: 'Back.easeOut',
      });
      // Color flash ring when each dino lands.
      this.time.delayedCall(350 + i * 140 + 320, () => {
        const ring = this.add
          .circle(cx, lineupY + 4, 4, DINOS[id].stripe)
          .setStrokeStyle(2, DINOS[id].stripe)
          .setFillStyle(DINOS[id].stripe, 0);
        this.tweens.add({
          targets: ring,
          radius: 30,
          alpha: { from: 0.9, to: 0 },
          duration: 600,
          onComplete: () => ring.destroy(),
        });
      });
      // Stripe under each dino
      this.add.rectangle(cx, lineupY + 22, 28, 3, DINOS[id].stripe);
    });

    // Confetti — tinted stars raining from above on a continuous loop.
    for (let i = 0; i < 50; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(-40, -10);
      const planet = DINO_ORDER[i % 4];
      const c = this.add
        .image(x, y, 'star')
        .setScale(Phaser.Math.FloatBetween(0.6, 1.4))
        .setTint(DINOS[planet].stripe);
      this.tweens.add({
        targets: c,
        y: GAME_HEIGHT + 20,
        rotation: Math.PI * 4,
        duration: Phaser.Math.Between(2200, 4400),
        delay: Phaser.Math.Between(0, 1200),
        repeat: -1,
        ease: 'Linear',
      });
    }

    // Fireworks bursts — three timed pops at planet-stripe colors.
    [800, 1500, 2300].forEach((delay, i) => {
      this.time.delayedCall(delay, () => {
        const fx = Phaser.Math.Between(60, GAME_WIDTH - 60);
        const fy = Phaser.Math.Between(40, 100);
        const color = DINOS[DINO_ORDER[i % 4]].stripe;
        for (let k = 0; k < 14; k++) {
          const angle = (k / 14) * Math.PI * 2;
          const spark = this.add.image(fx, fy, 'star').setTint(color).setScale(1);
          this.tweens.add({
            targets: spark,
            x: fx + Math.cos(angle) * 50,
            y: fy + Math.sin(angle) * 50,
            alpha: 0,
            scale: 0.2,
            duration: 700,
            ease: 'Cubic.easeOut',
            onComplete: () => spark.destroy(),
          });
        }
      });
    });

    // Score panel.
    this.add
      .text(GAME_WIDTH / 2 - 70, 134, 'TOTAL SCORE', {
        fontFamily: '"VT323", monospace',
        fontSize: '9px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0.85);
    const scoreText = this.add
      .text(GAME_WIDTH / 2 - 70, 158, '0000', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '20px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const scoreObj = { val: 0 };
    this.tweens.add({
      targets: scoreObj,
      val: score,
      delay: 900,
      duration: 1400,
      ease: 'Cubic.easeOut',
      onUpdate: () => scoreText.setText(String(Math.round(scoreObj.val)).padStart(4, '0')),
    });

    // Time panel — only shown if we have a real start timestamp.
    if (runStartMs) {
      this.add
        .text(GAME_WIDTH / 2 + 70, 134, 'TIME', {
          fontFamily: '"VT323", monospace',
          fontSize: '9px',
          color: '#ffffff',
        })
        .setOrigin(0.5)
        .setAlpha(0.85);
      this.add
        .text(GAME_WIDTH / 2 + 70, 158, formatTime(totalTimeMs), {
          fontFamily: '"Press Start 2P", "VT323", monospace',
          fontSize: '18px',
          color: '#9ad8ff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
    }

    // Tagline.
    this.add
      .text(GAME_WIDTH / 2, 192, 'four planets. one team.', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0.6);

    // The big finale comms: Mika victory cheer, Mr Consequence final
    // meltdown, Bosco's proud closer.
    const comms = new CommsBox(this);
    comms.saySequence([
      COMMS.gauntletCleared(),
      { speaker: 'consequence' as const, text: 'NOOOO! ALL FOUR?! I will... I will be back!' },
      { speaker: 'bosco' as const, text: 'I am proud of you, captain. Truly.' },
    ]);

    // Wait for the comms to finish before showing the score-entry form so
    // the player gets the full celebratory beat first. Safety cap at 12s.
    let formShown = false;
    const showFormOnce = () => {
      if (formShown) return;
      formShown = true;
      this.showScoreForm(score, totalTimeMs, dinoId);
    };
    const waitForComms = this.time.addEvent({
      delay: 250,
      loop: true,
      callback: () => {
        if (comms.isIdle) {
          waitForComms.remove();
          showFormOnce();
        }
      },
    });
    this.time.delayedCall(12000, showFormOnce);

    // Cleanup the form + restore keyboard when the scene shuts down.
    const cleanup = () => {
      this.removeForm();
      if (this.input.keyboard) this.input.keyboard.enabled = true;
    };
    this.events.on('shutdown', cleanup);
    this.events.on('destroy', cleanup);

    void dinoId;
  }

  /**
   * Build a vanilla HTML overlay for the score entry. Phaser DOM containers
   * aren't enabled in this project, so we append to document.body and rely
   * on viewport-fixed CSS centering. Removed via removeForm() on submit
   * or on scene shutdown.
   */
  private showScoreForm(score: number, timeMs: number, dinoId: DinoId) {
    const dim = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0d0a1a, 0.55)
      .setOrigin(0, 0)
      .setDepth(900)
      .setAlpha(0);
    this.tweens.add({ targets: dim, alpha: 0.7, duration: 300 });

    // Disable Phaser's keyboard plugin AND clear all key captures while
    // the HTML form is up. Phaser registers captures GLOBALLY (at the
    // KeyboardManager level) when scenes call addKeys/addKey — that means
    // even after we set enabled=false, preventDefault() still fires on
    // captured keys (A, S, D, TAB, ENTER, etc.) and they never reach the
    // form input. clearCaptures() removes the global preventDefault list;
    // captures get re-registered automatically when the next scene's
    // create() runs and calls addKeys again.
    if (this.input.keyboard) {
      this.input.keyboard.clearCaptures();
      this.input.keyboard.enabled = false;
    }

    // If the leaderboard isn't configured (no env vars), skip straight to
    // the play-again buttons. Hides the feature instead of erroring.
    if (!isLeaderboardConfigured()) {
      if (this.input.keyboard) this.input.keyboard.enabled = true;
      this.showResultButtons();
      return;
    }

    const el = document.createElement('div');
    el.className = 'astrodinos-score-form';
    el.innerHTML = `
      <style>
        .astrodinos-score-form{
          position:fixed; inset:0; display:flex; align-items:center; justify-content:center;
          z-index:9999; pointer-events:none;
          font-family:"VT323", "Press Start 2P", monospace;
          opacity:0; transition:opacity 280ms ease;
        }
        .astrodinos-score-form.show{ opacity:1; }
        .astrodinos-score-form .panel{
          pointer-events:auto;
          background:#0d0a1a; border:3px solid #ffe23a;
          padding:18px 22px; min-width:300px; max-width:420px;
          color:#fff; box-shadow:0 0 0 2px #000, 0 0 30px rgba(255,226,58,0.3);
          image-rendering:pixelated;
        }
        .astrodinos-score-form h2{
          margin:0 0 12px; color:#ffe23a; font-size:18px; letter-spacing:1px;
          text-align:center;
        }
        .astrodinos-score-form .row{ margin:10px 0; }
        .astrodinos-score-form label{
          display:block; font-size:13px; color:#9ad8ff; margin-bottom:4px;
          text-transform:uppercase; letter-spacing:1px;
        }
        .astrodinos-score-form input[type=text],
        .astrodinos-score-form input[type=email]{
          width:100%; box-sizing:border-box;
          background:#1a1530; color:#fff; border:2px solid #444466;
          padding:8px 10px; font-size:16px; font-family:inherit;
          outline:none;
        }
        .astrodinos-score-form input:focus{ border-color:#ffe23a; }
        .astrodinos-score-form .prize{ display:flex; align-items:center; gap:8px; margin-top:6px; font-size:13px; color:#ddd; cursor:pointer; }
        .astrodinos-score-form .prize input{ width:16px; height:16px; }
        .astrodinos-score-form .stats{
          background:#1a1530; padding:8px 10px; margin-bottom:12px; font-size:14px;
          display:flex; justify-content:space-between; color:#ddd;
        }
        .astrodinos-score-form .stats b{ color:#ffe23a; }
        .astrodinos-score-form .submit{
          background:#ffe23a; color:#0d0a1a; border:none; padding:10px 20px;
          font-size:16px; font-family:inherit; font-weight:bold;
          cursor:pointer; width:100%; margin-top:8px;
          letter-spacing:1px;
        }
        .astrodinos-score-form .submit:hover{ background:#fff066; }
        .astrodinos-score-form .submit:disabled{ opacity:0.5; cursor:wait; }
        .astrodinos-score-form .skip{
          display:block; margin:10px auto 0; background:none; border:none;
          color:#888; font-family:inherit; font-size:12px; cursor:pointer; text-decoration:underline;
        }
        .astrodinos-score-form .err{ color:#ff5a3a; font-size:12px; margin-top:6px; min-height:14px; }
      </style>
      <div class="panel">
        <h2>POST YOUR SCORE</h2>
        <div class="stats">
          <span>SCORE <b>${String(score).padStart(4, '0')}</b></span>
          <span>TIME <b>${formatTime(timeMs)}</b></span>
        </div>
        <div class="row">
          <label for="ad-name">Name (max 24 chars)</label>
          <input type="text" id="ad-name" maxlength="24" autocomplete="off" autofocus>
        </div>
        <div class="row">
          <label for="ad-email">Email (optional)</label>
          <input type="email" id="ad-email" maxlength="200" autocomplete="off">
          <label class="prize">
            <input type="checkbox" id="ad-prize">
            <span>Enter me in the next prize draw</span>
          </label>
        </div>
        <div class="err" id="ad-err"></div>
        <button class="submit" id="ad-submit">SUBMIT</button>
        <button class="skip" id="ad-skip">skip — don't post my score</button>
      </div>
    `;
    document.body.appendChild(el);
    this.formEl = el;
    requestAnimationFrame(() => el.classList.add('show'));

    const nameInput = el.querySelector<HTMLInputElement>('#ad-name')!;
    const emailInput = el.querySelector<HTMLInputElement>('#ad-email')!;
    const prizeCheck = el.querySelector<HTMLInputElement>('#ad-prize')!;
    const errEl = el.querySelector<HTMLDivElement>('#ad-err')!;
    const submitBtn = el.querySelector<HTMLButtonElement>('#ad-submit')!;
    const skipBtn = el.querySelector<HTMLButtonElement>('#ad-skip')!;

    nameInput.focus();
    // Pressing Enter inside any field submits the form.
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void onSubmit();
      }
    });

    const onSubmit = async () => {
      const name = nameInput.value.trim();
      const email = emailInput.value.trim();
      const optInPrize = prizeCheck.checked;
      if (!name) {
        errEl.textContent = 'Please enter a name.';
        nameInput.focus();
        return;
      }
      if (optInPrize && !email) {
        errEl.textContent = 'Email is needed for the prize draw.';
        emailInput.focus();
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'SUBMITTING...';
      errEl.textContent = '';
      try {
        await submitScore({ name, email, score, timeMs, optInPrize });
        this.removeForm();
        if (this.input.keyboard) this.input.keyboard.enabled = true;
        this.showLeaderboard(name, score, timeMs);
      } catch (e) {
        errEl.textContent = `Couldn't submit (${(e as Error).message}). Try again?`;
        submitBtn.disabled = false;
        submitBtn.textContent = 'SUBMIT';
      }
    };
    submitBtn.addEventListener('click', () => void onSubmit());
    skipBtn.addEventListener('click', () => {
      this.removeForm();
      if (this.input.keyboard) this.input.keyboard.enabled = true;
      this.showResultButtons();
    });

    // Tag dinoId so TypeScript doesn't complain about unused param when this
    // file is read in isolation; it's also useful for future per-dino flair.
    void dinoId;
  }

  private removeForm() {
    if (this.formEl && this.formEl.parentNode) {
      this.formEl.parentNode.removeChild(this.formEl);
    }
    this.formEl = null;
  }

  /**
   * Replace the form with a top-10 leaderboard view. Player's row is
   * highlighted by name match if they made the top 10; otherwise we fetch
   * and display their actual rank below the table so they always see how
   * they did, even on a low score. Pass timeMs so the rank lookup uses
   * the same score+time tiebreaker the top-10 ordering uses.
   */
  private async showLeaderboard(highlightName: string, score: number, timeMs: number) {
    const titleY = 28;
    // Clear the previous "GAUNTLET CLEARED" so the leaderboard has room.
    // (We don't actually have a reference to it here, but the table is
    // rendered as fresh objects on top so it visually overrides.)
    const dim = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0d0a1a, 0.85)
      .setOrigin(0, 0)
      .setDepth(900);

    const heading = this.add
      .text(GAME_WIDTH / 2, titleY, 'TOP 10 GAUNTLET RUNS', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '12px',
        color: '#ffe23a',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(901);

    const loadingText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'loading scores...', {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(901);

    const rows: Phaser.GameObjects.GameObject[] = [];
    try {
      const scores = await fetchTopScores(10);
      loadingText.destroy();
      this.renderLeaderboardRows(scores, highlightName, rows, 901);
      // If the player's score didn't make the visible top 10, fetch their
      // actual rank so they get feedback. Done in parallel-ish: we
      // re-await but it's a fast count query.
      const inTop10 = scores.some((s) => s.name === highlightName && s.score === score);
      if (!inTop10) {
        try {
          const { rank, total } = await fetchRankFor(score, timeMs);
          this.renderRankFooter(rank, total, score);
        } catch {
          // Quietly skip — the top 10 is still useful even without rank.
        }
      }
    } catch (e) {
      loadingText.setText(`couldn't load scores (${(e as Error).message})`);
      loadingText.setColor('#ff5a3a');
    }

    // After-leaderboard buttons. Depth 902 lifts both rect AND text above
    // the dim overlay (depth 900); without passing depth into makeButton
    // the labels stayed at default 0 and were hidden by the dim.
    void dim;
    void heading;
    this.makeButton(
      GAME_WIDTH / 2 - 60,
      GAME_HEIGHT - 22,
      'AGAIN',
      0xffe23a,
      () => this.scene.start('Game', { dinoId: 'doux', mode: 'gauntlet', runScore: 0 }),
      902,
    );
    this.makeButton(
      GAME_WIDTH / 2 + 60,
      GAME_HEIGHT - 22,
      'LEVELS',
      0x444466,
      () => this.scene.start('CharacterSelect'),
      902,
    );
  }

  private renderLeaderboardRows(
    scores: LeaderboardEntry[],
    highlightName: string,
    rows: Phaser.GameObjects.GameObject[],
    depth: number,
  ) {
    const startY = 56;
    const rowH = 16;
    // Header row
    const headerStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: '"VT323", monospace',
      fontSize: '10px',
      color: '#9ad8ff',
    };
    rows.push(
      this.add.text(28, startY, '#', headerStyle).setDepth(depth),
      this.add.text(56, startY, 'NAME', headerStyle).setDepth(depth),
      this.add.text(280, startY, 'SCORE', headerStyle).setDepth(depth).setOrigin(1, 0),
      this.add.text(GAME_WIDTH - 30, startY, 'TIME', headerStyle).setDepth(depth).setOrigin(1, 0),
    );

    if (!scores.length) {
      rows.push(
        this.add
          .text(GAME_WIDTH / 2, startY + 30, 'no scores yet — be the first!', {
            fontFamily: '"VT323", monospace',
            fontSize: '11px',
            color: '#ffffff',
          })
          .setOrigin(0.5)
          .setDepth(depth),
      );
      return;
    }

    scores.forEach((entry, i) => {
      const y = startY + 16 + i * rowH;
      const isHighlight = entry.name === highlightName;
      const color = isHighlight ? '#ffe23a' : '#ffffff';
      const style: Phaser.Types.GameObjects.Text.TextStyle = {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color,
        fontStyle: isHighlight ? 'bold' : 'normal',
      };
      if (isHighlight) {
        rows.push(
          this.add
            .rectangle(GAME_WIDTH / 2, y + 6, GAME_WIDTH - 24, rowH, 0xffe23a, 0.15)
            .setDepth(depth),
        );
      }
      rows.push(
        this.add.text(28, y, String(i + 1).padStart(2, ' '), style).setDepth(depth + 1),
        this.add.text(56, y, entry.name, style).setDepth(depth + 1),
        this.add
          .text(280, y, String(entry.score).padStart(4, '0'), style)
          .setDepth(depth + 1)
          .setOrigin(1, 0),
        this.add
          .text(GAME_WIDTH - 30, y, formatTime(entry.timeMs), style)
          .setDepth(depth + 1)
          .setOrigin(1, 0),
      );
    });
  }

  /**
   * Footer shown when the player's score didn't crack the visible top 10.
   * Tells them their actual rank + total entries so they always get
   * feedback even on a low score. Sits between the table and the buttons.
   */
  private renderRankFooter(rank: number, total: number, score: number) {
    const y = 220;
    this.add
      .text(GAME_WIDTH / 2, y, 'YOUR SCORE', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#9ad8ff',
      })
      .setOrigin(0.5)
      .setDepth(901);
    this.add
      .text(GAME_WIDTH / 2, y + 14, `${String(score).padStart(4, '0')}   —   RANK ${rank} / ${total}`, {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '11px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(901);
    this.add
      .text(GAME_WIDTH / 2, y + 30, 'better luck next gauntlet!', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0.8)
      .setDepth(901);
  }

  /** Skip-leaderboard fallback: just show AGAIN/LEVELS buttons. */
  private showResultButtons() {
    this.makeButton(GAME_WIDTH / 2 - 60, GAME_HEIGHT - 22, 'AGAIN', 0xffe23a, () =>
      this.scene.start('Game', { dinoId: 'doux', mode: 'gauntlet', runScore: 0 }),
    );
    this.makeButton(GAME_WIDTH / 2 + 60, GAME_HEIGHT - 22, 'LEVELS', 0x444466, () =>
      this.scene.start('CharacterSelect'),
    );
    this.input.keyboard?.once('keydown-ENTER', () =>
      this.scene.start('Game', { dinoId: 'doux', mode: 'gauntlet', runScore: 0 }),
    );
    this.input.keyboard?.once('keydown-ESC', () => this.scene.start('CharacterSelect'));
  }

  private makeButton(
    x: number,
    y: number,
    label: string,
    color: number,
    onClick: () => void,
    depth = 0,
  ) {
    const w = 90;
    const h = 22;
    const bg = this.add
      .rectangle(x, y, w, h, color)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(depth);
    // The text needs the same depth as its rectangle — otherwise on the
    // post-submit leaderboard view (which sits behind a depth-900 dim
    // overlay) the label would be hidden under the dim while the bg
    // stayed visible, leaving "blank" buttons.
    this.add
      .text(x, y, label, {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: color === 0xffe23a ? '#0d0a1a' : '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(depth);
    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setScale(1.05));
    bg.on('pointerout', () => bg.setScale(1));
    bg.on('pointerdown', onClick);
    return bg;
  }
}
